import {
  Button,
  DatePickerDropdown,
  HSpacer,
  Modal,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { ModalProps } from '@/components/DesignSystem/Modal/Modal';
import { Stack } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DateRangeModalProps extends ModalProps {
  acceptButtonLabel: string,
  initialEndDate?: Date,
  initialStartDate?: Date,
  maxDate?: Date,
  minDate?: Date,
  onClose: () => void,
  onConfirm: (startDate: Date, endDate: Date) => void,
  outOfRangeErrorMessage?: string,
  testID: string,
}

export const DateRangeModal = ({
  acceptButtonLabel = 'Confirm',
  initialEndDate,
  initialStartDate,
  maxDate,
  minDate,
  onClose,
  onConfirm,
  outOfRangeErrorMessage,
  testID,
  ...rest
}: DateRangeModalProps) => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<Date | undefined>(initialStartDate ?? new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(initialEndDate ?? new Date());

  const toDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const isOutOfRange = (
    (!!minDate && !!startDate && toDay(startDate) < toDay(minDate))
    || (!!maxDate && !!endDate && toDay(endDate) > toDay(maxDate))
  );

  const isOrderInvalid = !!startDate && !!endDate && toDay(startDate) > toDay(endDate);

  const isFormValid = !!startDate && !!endDate && !isOutOfRange && !isOrderInvalid;

  const orderInvalidMessage = isOrderInvalid
    ? t($ => $.common.validation.invalidDates)
    : undefined;

  return (
    <Modal
      acceptButton={(props) => (
        <Button
          {...props}
          disabled={!isFormValid}
          onClick={() => onConfirm(startDate!, endDate!)}
        >
          {acceptButtonLabel}
        </Button>
      )}
      cancelButton={(props) => (
        <Button {...props} onClick={onClose}>
          {t($ => $.common.actions.close)}
        </Button>
      )}
      onClose={onClose}
      testID={testID}
      {...rest}
    >
      <Stack direction="column">
        <Stack direction="row">
          <DatePickerDropdown
            errorMessage={orderInvalidMessage}
            label={t($ => $.shared.dateRange.startDate)}
            onChange={(startDate) => setStartDate(startDate)}
            testID={`${testID}-start-date`}
            value={startDate}
          />
          <HSpacer size="7"/>
          <DatePickerDropdown
            errorMessage={orderInvalidMessage}
            label={t($ => $.shared.dateRange.endDate)}
            onChange={(endDate) => setEndDate(endDate)}
            testID={`${testID}-end-date`}
            value={endDate}
          />
        </Stack>
        {isOutOfRange && (
          <>
            <VSpacer size="3" />
            <Text category="body-small" sx={{ color: 'error.main' }}>
              {outOfRangeErrorMessage ?? t($ => $.common.validation.invalidDates)}
            </Text>
          </>
        )}
      </Stack>
    </Modal>
  );
};
